/**
 * Analytics — dual tracking to localStorage (for admin panel) and Plausible (production).
 *
 * Plausible is loaded via a script tag in public/index.html, so it's always on in production.
 * This module provides logEvent(), which writes to both localStorage (for the admin
 * analytics tab) and Plausible (for production dashboards).
 */

// Per-tab session ID — stable for the life of this browser tab. Lets
// downstream queries group events into sessions cleanly for cohort + fatigue
// analysis without relying on heuristic time-gap segmentation.
//
// Persisted in sessionStorage so reloads within the same tab keep the same
// session (matches how the "Session actions per minute" metric expects it).
// Opening a new tab = new session, which is the right semantic — each tab
// is usually a distinct task.
function getSessionId() {
  if (typeof window === "undefined") return null;
  try {
    let sid = sessionStorage.getItem("bam_session_id");
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("bam_session_id", sid);
    }
    return sid;
  } catch {
    return null;
  }
}

function currentPlatform() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    const p = cap && typeof cap.getPlatform === "function" ? cap.getPlatform() : "web";
    return p === "ios" || p === "android" ? p : "web";
  } catch { return "web"; }
}

// What may leave the device with an event (PQ-27, analytics inventory A1):
//   - never, anywhere: who someone is (email, names, phone) or which player
//     (player ids). An old caller that still passes them is cleaned here.
//   - Plausible gets less again. Its props are third-party, aggregate and
//     meant to be low-cardinality, so no free text (search terms, notes),
//     no ids of people, sessions or anything coach-made, and no long strings.
//     Our own content's ids and titles are fine: they're what "top videos"
//     is made of. Our analytics_events table (admin-read only) keeps the rest,
//     including the search term for the search-gaps room.
const NEVER_SEND = /^(email|e_mail|user_email|name|first_name|last_name|full_name|display_name|player_name|player_id|phone)$/i;
const FREE_TEXT = /^(term|query|q|search|text|body|content|note|notes|message|prompt|question)$/i;
const CONTENT_IDS = /^(content_id|contentId|drill_id|drillId|drop_id|dropId|masterclass_id)$/;
const looksLikeEmail = v => typeof v === "string" && /[^\s@]+@[^\s@]+\.[^\s@]+/.test(v);

/** Metadata for our own analytics_events table. */
export function forStorage(data) {
  const out = {};
  Object.entries(data || {}).forEach(([k, v]) => {
    if (NEVER_SEND.test(k) || looksLikeEmail(v)) return;
    out[k] = v;
  });
  return out;
}

/** Props for Plausible: flat, short, no free text, no ids. */
export function forPlausible(data) {
  const out = {};
  Object.entries(forStorage(data)).forEach(([k, v]) => {
    if (FREE_TEXT.test(k)) return;
    if ((/(^|_)id$/i.test(k) || /Id$/.test(k)) && !CONTENT_IDS.test(k)) return;
    if (v === null || v === undefined || typeof v === "object") return;
    if (typeof v === "string" && v.length > 60) return;
    out[k] = v;
  });
  return out;
}

/**
 * logEvent — track an event to both localStorage (admin/debug) and Plausible (production).
 *
 * @param {string} name  - event name (e.g. "signup_started", "checkout_completed")
 * @param {object} [data] - optional properties (kept as Plausible "props")
 */
export function logEvent(name, data = {}) {
  const sessionId = getSessionId();
  // Always include session_id in metadata so server-side queries can group.
  // Client-only loggers (localStorage, Plausible) get it too for consistency.
  // Which surface it happened on, "ios" (the app) or "web" (Coleman,
  // 2026-10-15: week-one app installs and app vs web retention). The
  // device type only, alongside usage the app already logs.
  const withPlatform = { platform: currentPlatform(), ...forStorage(data) };
  const enrichedData = sessionId ? { ...withPlatform, session_id: sessionId } : withPlatform;
  // 1. localStorage (used by admin panel analytics view — kept as fast fallback)
  try {
    const events = JSON.parse(localStorage.getItem("bam_analytics_events") || "[]");
    events.push({ name, ...enrichedData, ts: Date.now() });
    if (events.length > 500) events.splice(0, events.length - 500);
    localStorage.setItem("bam_analytics_events", JSON.stringify(events));
  } catch {}

  // 1b. Supabase analytics_events via the log-analytics Edge Function.
  // The Edge Function writes with service_role (bypasses RLS) so inserts
  // succeed regardless of the browser's auth state. Gives us persistent
  // per-user event history across devices. Fails silently — localStorage
  // + Plausible are the authoritative sources of truth.
  try {
    const { supabase } = require("../../supabaseClient");
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (supabaseUrl && anonKey) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        const authHeader = session?.access_token
          ? `Bearer ${session.access_token}`
          : `Bearer ${anonKey}`;
        fetch(`${supabaseUrl}/functions/v1/log-analytics`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": anonKey,
            "Authorization": authHeader,
          },
          body: JSON.stringify({ event_type: name, metadata: enrichedData }),
          keepalive: true, // survive page unloads during navigation events
        }).catch(() => {});
      }).catch(() => {});
    }
  } catch {}

  // 2. Plausible (loaded by script tag in index.html, always defined)
  if (typeof window !== "undefined" && typeof window.plausible === "function") {
    try {
      // Plausible's custom-event call signature: plausible('Event Name', { props: {...} })
      const safe = forPlausible(enrichedData);
      const props = Object.keys(safe).length > 0 ? { props: safe } : undefined;
      window.plausible(name, props);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] plausible call failed", e);
    }
  }
}

/**
 * FunnelEvent — canonical event names. Use these constants instead of string
 * literals so any tooling that walks the codebase can find all tracked events.
 *
 * Event groups:
 *   - Funnel:       signup → paywall → checkout
 *   - Product:      plans, cert, content, community
 *   - Retention:    streaks, return visits
 *   - Fatigue:      cognitive-load behavioral signatures (see analytics-signals.js)
 */
export const FunnelEvent = {
  // ── Acquisition funnel ────────────────────────────────────────────────
  SignupStarted: "signup_started",
  SignupCompleted: "signup_completed",
  PaywallShown: "paywall_shown",
  CheckoutStarted: "checkout_started",
  CheckoutCompleted: "checkout_completed",
  CheckoutCancelled: "checkout_cancelled",
  ProfileSetupCompleted: "profile_setup_completed",

  // ── Practice plan lifecycle ───────────────────────────────────────────
  PlanBuilderOpened: "plan_builder_opened",
  PlanGenerated: "plan_generated",           // AI or template
  PlanSaved: "plan_saved",
  PlanCleared: "plan_cleared",
  PlanExportedPdf: "plan_exported_pdf",
  PlanTemplateLoaded: "plan_template_loaded",
  BlockAdded: "block_added",
  BlockDuplicated: "block_duplicated",
  BlockDeleted: "block_deleted",
  ConstraintAdded: "constraint_added",       // source: bank|ai|custom
  ConstraintAISuggested: "constraint_ai_suggested",

  // ── Advantage Flow (brief 774; the full list and labels: film/afsFunnelSteps.js) ──
  AfsLandingView: "afs_landing_view",
  AfsGetStartedOpen: "afs_get_started_open",
  AfsLead: "afs_lead",
  AfsPriceShown: "afs_price_shown",
  AfsCheckoutStart: "afs_checkout_start",
  AfsPurchase: "afs_purchase",
  AfsTrialClaimed: "afs_trial_claimed",
  AfsFirstGame: "afs_first_game",
  AfsSecondLookRun: "afs_second_look_run",

  // ── Certification ─────────────────────────────────────────────────────
  CertLandingViewed: "cert_landing_viewed",
  CertEnrolled: "cert_enrolled",
  CertUnitStarted: "cert_unit_started",
  CertLessonCompleted: "cert_lesson_completed",
  CertUnitCompleted: "cert_unit_completed",
  CertQuizSubmitted: "cert_quiz_submitted",
  CertQuizPassed: "cert_quiz_passed",
  CertExamSubmitted: "cert_exam_submitted",
  CertCompleted: "cert_completed",

  // ── Content engagement ────────────────────────────────────────────────
  VideoPlayed: "video_played",
  VideoProgress25: "video_progress_25",
  VideoProgress75: "video_progress_75",
  VideoCompleted: "video_completed",
  DrillOpened: "drill_opened",
  MasterclassViewed: "masterclass_viewed",
  ContentSaved: "content_saved",
  ContentSubmitted: "content_submitted",

  // ── Community ─────────────────────────────────────────────────────────
  PostCreated: "post_created",
  ReplyCreated: "reply_created",
  LikeGiven: "like_given",
  ProfileVisited: "profile_visited",

  // ── Retention ─────────────────────────────────────────────────────────
  ReturnVisit: "return_visit",
  StreakHit: "streak_hit",                   // length: 2,7,14,30
  FeatureFirstUse: "feature_first_use",      // feature: plans|cert|askbam|devstudio|community

  // ── AI feature usage ──────────────────────────────────────────────────
  AskBamOpened: "ask_bam_opened",
  AskBamQuestion: "ask_bam_question",
  DevStudioPolish: "devstudio_polish",
  DevStudioKpisGenerated: "devstudio_kpis_generated",
  DevStudioRoadmapGenerated: "devstudio_roadmap_generated",

  // ── Decision-fatigue signals ─────────────────────────────────────────
  // (also see analytics-signals.js helpers which wrap emission of these)
  IdleOnScreen: "idle_on_screen",            // >60s without input on editable screen
 UndoBurst: "undo_burst", // 3+ undos within 5s: frustration signal
 FilterToggledRapid: "filter_toggled_rapid",// <3s between on→off: indecision
  AiSuggestionRejected: "ai_suggestion_rejected_all", // opened, added none
  AiPlanRegenerated: "ai_plan_regenerated",  // >1 attempts for same session
  DefaultKept: "default_kept",               // saved AI output unchanged
  EditDepth: "edit_depth",                   // count of field-level edits before save
  FlowAbandoned: "flow_abandoned",           // modal/builder opened, closed without completion
  SessionActionCount: "session_action_count",// on session_end: total actions + duration
};
